import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  Link as MuiLink,
  CircularProgress,
  Divider,
} from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Error, Email, Refresh } from '@mui/icons-material';

const EmailVerification = () => {
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);
  
  const { key } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      if (!key) {
        setStatus('error');
        setMessage('Invalid verification link. No verification key found.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch('/api/accounts/email/verify-email/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ key }),
        });

        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(data.message || 'Your email has been successfully verified!');
        } else {
          if (data.error && data.error.includes('expired')) {
            setStatus('expired');
            setMessage('Your verification link has expired. Please request a new verification email.');
          } else {
            setStatus('error');
            setMessage(data.error || 'Email verification failed. The link may be invalid or expired.');
          }
        }
      } catch (error) {
        setStatus('error');
        setMessage('An error occurred while verifying your email. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [key]);

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      // Note: This would require authentication, so we'll just show instructions for now
      setMessage('To resend verification email, please log in to your account and visit your profile settings.');
    } catch (error) {
      setMessage('Unable to resend verification email. Please log in to your account.');
    } finally {
      setResendLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />;
      case 'error':
      case 'expired':
        return <Error sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />;
      case 'verifying':
      default:
        return <CircularProgress size={80} sx={{ mb: 2 }} />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
      case 'expired':
        return 'error';
      case 'verifying':
      default:
        return 'info';
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Email Verified Successfully';
      case 'error':
        return 'Verification Failed';
      case 'expired':
        return 'Link Expired';
      case 'verifying':
      default:
        return 'Verifying Your Email';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper 
            elevation={0}
            sx={{ 
              padding: 6, 
              width: '100%',
              maxWidth: 600,
              background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                component="h1" 
                variant="h3" 
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Wizcord
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Discord Integration Platform
              </Typography>
            </Box>

            <Divider sx={{ mb: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Status Icon */}
            <Box sx={{ mb: 3 }}>
              {getStatusIcon()}
            </Box>

            {/* Title */}
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                mb: 3,
                color: 'text.primary',
              }}
            >
              {getTitle()}
            </Typography>

            {/* Message */}
            <Alert 
              severity={getStatusColor()} 
              sx={{ 
                mb: 4,
                borderRadius: 2,
                background: `rgba(${status === 'success' ? '16, 185, 129' : status === 'error' || status === 'expired' ? '239, 68, 68' : '59, 130, 246'}, 0.1)`,
                border: `1px solid rgba(${status === 'success' ? '16, 185, 129' : status === 'error' || status === 'expired' ? '239, 68, 68' : '59, 130, 246'}, 0.2)`,
                '& .MuiAlert-message': {
                  fontWeight: 500,
                  fontSize: '1rem',
                },
              }}
            >
              {message}
            </Alert>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {status === 'success' && (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/login')}
                    sx={{ 
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)',
                      },
                    }}
                  >
                    Continue to Login
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Your email is now verified and you can sign in to your account.
                  </Typography>
                </>
              )}

              {(status === 'error' || status === 'expired') && (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    startIcon={resendLoading ? <CircularProgress size={20} /> : <Refresh />}
                    sx={{ 
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)',
                      },
                      '&:disabled': {
                        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.5) 0%, rgba(139, 92, 246, 0.5) 100%)',
                      },
                    }}
                  >
                    {resendLoading ? 'Processing...' : 'Get Help with Verification'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/login')}
                    sx={{ 
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderColor: 'rgba(99, 102, 241, 0.5)',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      },
                    }}
                  >
                    Back to Login
                  </Button>
                </>
              )}

              {status === 'verifying' && (
                <Typography variant="body2" color="text.secondary">
                  Please wait while we verify your email address...
                </Typography>
              )}
            </Box>

            {/* Help Section */}
            <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Box 
              sx={{ 
                p: 3,
                borderRadius: 2,
                background: 'rgba(99, 102, 241, 0.05)',
                border: '1px solid rgba(99, 102, 241, 0.1)',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Need help with your account?
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <MuiLink 
                  component={Link} 
                  to="/support"
                  sx={{
                    color: 'primary.light',
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main',
                    },
                  }}
                >
                  Contact Support
                </MuiLink>
                <Typography variant="body2" color="text.secondary">•</Typography>
                <MuiLink 
                  component={Link} 
                  to="/register"
                  sx={{
                    color: 'primary.light',
                    textDecoration: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                      color: 'primary.main',
                    },
                  }}
                >
                  Create New Account
                </MuiLink>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default EmailVerification; 